/**
 * The shape of an axis cell's context menu: which options it lists, in what order, with what
 * labels, and which of them are hidden or disabled.
 *
 * This is derived entirely from the cell's {@link AxisCellModel} plus the table-level context it is
 * rendered in, so the menu can be reasoned about and tested without rendering `TableAxisCell`. The
 * click handlers are deliberately not built here: each built-in entry names the action it performs
 * with an {@link AxisCellMenuItem} and carries the arguments that action needs, and `TableAxisCell`
 * attaches the handler.
 */

import {ClickableContextMenuOption, ContextMenuDisplayGroup, ContextMenuOption} from 'components/ContextMenu';
import {
  SORT_ASCENDING_ID,
  SORT_DESCENDING_ID,
} from 'notebook/structured-tables/column-sorting/getColumnSortingMenuOptions';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {ColumnConstraint, InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {
  ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
  ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID,
  CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID,
  INSERT_CUSTOM_COLUMN_LEFT,
  INSERT_CUSTOM_COLUMN_RIGHT,
  INSERT_CUSTOM_SINGLE_INPUT_LEFT,
  INSERT_CUSTOM_SINGLE_INPUT_RIGHT,
  REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
  SINGLE_INPUT_CONTEXT_MENU_OPTION_ID,
} from 'notebook/tables/constants/TableContextMenuConstants';
import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {
  SelectionRange,
  TABLE_COLUMN,
  TABLE_ROW,
  TableDimension,
} from 'notebook/tables/constants/TableSelectionConstants';
import {
  AxisCellModel,
  isColumnHeaderModel,
  isRowHeaderModel,
} from 'notebook/tables/store/models/AxisCellModel';
import getDimensionalOperationText from 'notebook/tables/util/getDimensionalOperationText';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

// TODO(BNCH-108093): The ordering of the context menu options is expressed as the order this module
// assembles them in, interleaved with the display groups the extra options declare. Reordering the
// options is still harder than it should be.

// When a table is protected because it's a fill-in entry, we should only allow column context menu
// options that cannot change the structure of the table.
export const ALLOWED_COLUMN_CONTEXT_MENU_OPTION_IDS_WHEN_PROTECTED = [
  ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID,
  SORT_ASCENDING_ID,
  SORT_DESCENDING_ID,
];

/** Which side of the selection a row/column would be inserted on. */
export type InsertDimDirection = 'BEFORE' | 'AFTER';

/** Every built-in option an axis cell's context menu can offer. */
export const enum AxisCellMenuItem {
  RENAME_DIM = 'RENAME_DIM',
  TOGGLE_HEADER = 'TOGGLE_HEADER',
  DELETE_TABLE = 'DELETE_TABLE',
  DELETE_DIM = 'DELETE_DIM',
  LOCK_COLUMN = 'LOCK_COLUMN',
  UNLOCK_COLUMN = 'UNLOCK_COLUMN',
  FREEZE_COLUMN = 'FREEZE_COLUMN',
  UNFREEZE_COLUMN = 'UNFREEZE_COLUMN',
  INSERT_DIM_BEFORE = 'INSERT_DIM_BEFORE',
  INSERT_DIM_AFTER = 'INSERT_DIM_AFTER',
}

/** One of the specific columns an insert option offers in its submenu. */
export interface AxisCellMenuInsertOption {
  name: string;
  /**
   * The column to insert, already adjusted for where it is being inserted (a Single Input header
   * inserts Single Input columns), so that the handler passes it straight through.
   */
  columnInfo: InsertableColumnInfo;
  dataTestAttrs: DataTestAttrs;
}

/** One of the menu's built-in options. */
export interface AxisCellMenuItemEntry {
  kind: 'item';
  /** Which action the option performs, which is how the handler for it is looked up. */
  item: AxisCellMenuItem;
  name: string;
  /** Whether the option is omitted from the open menu. */
  isHidden: boolean;
  /** Whether the option is listed but cannot be chosen. */
  isDisabled: boolean;
  /**
   * Whether choosing the option performs its action. False only for an insert option disabled
   * because the table is already at its maximum size, which offers nothing to click.
   */
  isClickable: boolean;
  tooltip: string | null;
  dataTestAttrs: DataTestAttrs | null;
  /**
   * The rows/columns the option's action operates on, or null when it takes no range (or when
   * nothing is selected, in which case the action cannot be performed).
   */
  range: SelectionRange | null;
  /** The specific columns the option offers in a submenu, or null when it offers none. */
  insertOptions: Array<AxisCellMenuInsertOption> | null;
}

/** An option contributed by the table rather than by the axis cell, passed through as given. */
export interface AxisCellMenuExtraEntry {
  kind: 'extra';
  option: ClickableContextMenuOption;
}

/** A divider between groups of options. */
export interface AxisCellMenuSeparatorEntry {
  kind: 'separator';
}

export type AxisCellMenuEntry = AxisCellMenuItemEntry | AxisCellMenuExtraEntry | AxisCellMenuSeparatorEntry;

/**
 * Which dimension actions the axis cell's owner currently offers. These gate the options that
 * perform them: an option whose action is not offered is hidden rather than disabled.
 *
 * The row axis takes these from its own model (see `getPermittedRowDimActions`); the column axis's
 * depend on per-column constraints that `TableHeaderRow` holds and passes down as props.
 */
export interface AxisCellMenuActionAvailability {
  canRenameDim: boolean;
  canDeleteDim: boolean;
  canDeleteTable: boolean;
  canToggleHeader: boolean;
  canInsertDimBefore: boolean;
  canInsertDimAfter: boolean;
  canFreezeColumn: boolean;
  canUnfreezeColumn: boolean;
}

export interface AxisCellContextMenuInput {
  axisCellModel: AxisCellModel;
  availableActions: AxisCellMenuActionAvailability;
  /**
   * The extra options the table contributes for this cell's selection, in the order the table gave
   * them. They are re-grouped by their {@link ContextMenuDisplayGroup} rather than listed as given.
   */
  extraOptions: Array<ContextMenuOption>;
  /**
   * Whether this header labels a Single Input column, which names its insert options after single
   * inputs and inserts Single Input columns.
   */
  isSingleInputHeader: boolean;
  /**
   * Whether the current user may lock and unlock columns outside a configuration document, which
   * tenant admins may with `ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION`.
   */
  canModifyColumnLockAsAdmin: boolean;
}

/** How many rows/columns the given axis selection covers. */
export function getSelectedCount(selectionRange: SelectionRange | null): number {
  if (selectionRange == null) {
    return 0;
  }
  return selectionRange.maxIndex - selectionRange.minIndex + 1;
}

/**
 * Whether the column's lock/unlock options are offered at all. Locking is configuration, so outside
 * a configuration document it is offered only to admins who may edit column locking there. Only one
 * column can be locked at a time.
 */
export function computeCanSetProtected(
  isConfig: boolean,
  canModifyColumnLockAsAdmin: boolean,
  selectionRange: SelectionRange | null
): boolean {
  if (selectionRange == null) {
    return false;
  }
  return (isConfig || canModifyColumnLockAsAdmin) && selectionRange.maxIndex === selectionRange.minIndex;
}

/** Return the context menu delete column/row[s] text. */
export function getContextMenuDeleteDimsText(
  columnConstraint: ColumnConstraint | null,
  selectionRange: SelectionRange | null,
  dim: TableDimension
): string {
  const verb = columnConstraint?.viewer ? 'Hide' : 'Delete';
  return getDimensionalOperationText({
    verb,
    selectionRange,
    dimension: dim,
  });
}

/** Return the context menu insert column/row[s] text. */
export function getContextMenuInsertDimsText(
  dim: TableDimension,
  direction: InsertDimDirection,
  selectionRange: SelectionRange | null,
  disallowCustomColumns: boolean,
  // When true, the column noun is replaced with "single input" so that the menu reads
  // "Insert single input left" instead of "Insert column left".
  isSingleInput: boolean = false
): string {
  const dimension = isSingleInput ? 'single input' : dim === TABLE_ROW ? TABLE_ROW : TABLE_COLUMN;
  const directionText = {
    [TABLE_ROW]: {
      BEFORE: 'above',
      AFTER: 'below',
    },
    [TABLE_COLUMN]: {
      BEFORE: 'left',
      AFTER: 'right',
    },
  }[dim][direction];

  const numSelected = getSelectedCount(selectionRange);
  const allowMultiple = !disallowCustomColumns;
  if (numSelected === 1 || !allowMultiple) {
    return `Insert ${dimension} ${directionText}`;
  } else {
    return `Insert ${numSelected} ${dimension}s ${directionText}`;
  }
}

/**
 * Returns a copy of the given columnInfo with its column marked as a Single Input, so that the
 * inserted column is rendered as a Single Input. Returns the columnInfo unchanged if it does not
 * carry a structured table column (e.g. when inserting a blank/custom column).
 */
export function withSingleInputColumnInfo(columnInfo: InsertableColumnInfo | {}): InsertableColumnInfo | {} {
  const info = columnInfo as Partial<InsertableColumnInfo>;
  const structuredTableColumn = info.columnConstraints?.structuredTableColumn;
  if (structuredTableColumn == null) {
    return columnInfo;
  }
  return {
    ...info,
    columnConstraints: {
      ...info.columnConstraints,
      structuredTableColumn: {
        ...structuredTableColumn,
        isSingleInput: true,
      },
    },
  };
}

export function willInsertExceedTableMaxDims(
  dim: TableDimension,
  numDims: number,
  selectedCount: number
): boolean {
  if (dim === TABLE_ROW) {
    return numDims + selectedCount > getMaxRowsConfig(true);
  } else {
    return numDims + selectedCount > TableLayoutConstants.TABLE_MAX_COLUMNS;
  }
}

export function getContextMenuInsertDimsExceedMaxText(dim: TableDimension): string {
  const dimension = dim === TABLE_ROW ? TABLE_ROW : TABLE_COLUMN;
  const maxNumDims = dim === TABLE_ROW ? getMaxRowsConfig(true) : TableLayoutConstants.TABLE_MAX_COLUMNS;
  return `Total ${dimension}s after insert cannot exceed ${maxNumDims}`;
}

/** The extra options, bucketed into the groups the menu interleaves them into. */
interface GroupedExtraOptions {
  atTop: Array<ClickableContextMenuOption>;
  changeColumnType: ClickableContextMenuOption | null;
  atMiddleUpper: Array<ClickableContextMenuOption>;
  atMiddleLower: Array<ClickableContextMenuOption>;
  atBottomUpper: Array<ClickableContextMenuOption>;
  atBottomLower: Array<ClickableContextMenuOption>;
}

const MIDDLE_UPPER_OPTION_IDS = [
  ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID,
  ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
  REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
  SINGLE_INPUT_CONTEXT_MENU_OPTION_ID,
];

const BOTTOM_UPPER_OPTION_NAMES = [
  INSERT_CUSTOM_COLUMN_LEFT,
  INSERT_CUSTOM_SINGLE_INPUT_LEFT,
  INSERT_CUSTOM_COLUMN_RIGHT,
  INSERT_CUSTOM_SINGLE_INPUT_RIGHT,
];

function groupExtraOptions(
  extraOptions: Array<ContextMenuOption>,
  {isTableProtected}: {isTableProtected: boolean}
): GroupedExtraOptions {
  // Filter out any separators so that we only have clickable options
  let options = extraOptions.filter((option): option is ClickableContextMenuOption => option !== '-');
  // Filter out any options that are not allowed when the table is protected
  if (isTableProtected) {
    options = options.filter((option) =>
      ALLOWED_COLUMN_CONTEXT_MENU_OPTION_IDS_WHEN_PROTECTED.includes(option.id ?? '')
    );
  }

  return {
    // any options not specifying a displayGroup are placed at the top
    atTop: options.filter(
      (option) =>
        (!option.displayGroup || option.displayGroup === ContextMenuDisplayGroup.TOP) &&
        // Change column type option is manually positioned after rename, so exclude it from the
        // top group
        option.id !== CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID
    ),
    changeColumnType:
      options.find((option) => option.id === CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID) ?? null,
    atMiddleUpper: options.filter(
      (option) =>
        option.id &&
        MIDDLE_UPPER_OPTION_IDS.includes(option.id) &&
        option.displayGroup === ContextMenuDisplayGroup.MIDDLE
    ),
    atMiddleLower: options.filter(
      (option) =>
        (!option.id || !MIDDLE_UPPER_OPTION_IDS.includes(option.id)) &&
        option.displayGroup === ContextMenuDisplayGroup.MIDDLE
    ),
    atBottomUpper: options.filter(
      (option) =>
        BOTTOM_UPPER_OPTION_NAMES.includes(option.name) &&
        option.displayGroup === ContextMenuDisplayGroup.BOTTOM
    ),
    atBottomLower: options.filter(
      (option) =>
        !BOTTOM_UPPER_OPTION_NAMES.includes(option.name) &&
        option.displayGroup === ContextMenuDisplayGroup.BOTTOM
    ),
  };
}

/**
 * Computes the axis cell's context menu. Returns the entries in the order the menu lists them,
 * including the options it hides, since whether a cell offers a context menu at all is decided from
 * whether any of them is visible.
 */
export function computeAxisCellContextMenu({
  axisCellModel,
  availableActions,
  extraOptions,
  isSingleInputHeader,
  canModifyColumnLockAsAdmin,
}: AxisCellContextMenuInput): Array<AxisCellMenuEntry> {
  const {
    dim,
    selectionRange,
    isTableSelected,
    numDims,
    affixedLayout,
    shouldDisableDeleteDim,
    shouldDisableInsertDimBefore,
    shouldDisableInsertDimAfter,
    isLocked,
    isProtected,
  } = axisCellModel;
  const {isAffixed} = affixedLayout;
  // Only columns can be renamed, so the row axis never has an editable name to rename from, and
  // only columns are constrained. The insert options the row axis offers are likewise the column
  // axis's defaults: rows are always freeform, and the table never gives a reason not to insert.
  const columnHeader = isColumnHeaderModel(axisCellModel) ? axisCellModel : null;
  // Only the row axis can empty the table, and only the column axis can lock a column, so each of
  // these is constant on the other axis.
  const allowEmpty = isRowHeaderModel(axisCellModel) ? axisCellModel.allowEmpty : false;
  const isConfig = columnHeader?.isConfig ?? false;
  const columnConstraint = columnHeader?.columnConstraint ?? null;
  const disallowCustomColumns = columnHeader?.disallowCustomColumns ?? false;
  const insertableColumnInfos = columnHeader?.insertableColumnInfos;
  const disableInsertDimTooltip = columnHeader?.disableInsertDimTooltip;
  const disableColumnLocking = columnHeader?.disableColumnLocking ?? false;
  // When in Fill-In mode, the column context menu options are limited
  const isTableProtected = dim === TABLE_COLUMN && isProtected;
  const isReadOnly = columnConstraint?.structuredTableColumn?.isReadOnly ?? false;
  const isDimensionProtected = columnConstraint?.isProtected ?? false;
  // Tenant admins with ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION can lock/unlock columns
  // even when the table itself is protected against edit (e.g. an entry created from a
  // fill-in-only template). This override intentionally applies only to the lock/unlock options
  // below, not to the other protected-table gates (delete/insert dim, etc.) that also read
  // isTableProtected.
  const columnLockBlockedByTableProtection = isTableProtected && !canModifyColumnLockAsAdmin;
  const canSetProtected = computeCanSetProtected(isConfig, canModifyColumnLockAsAdmin, selectionRange);
  const isSingleInputColumnConstraint = columnConstraint?.structuredTableColumn?.isSingleInput === true;

  const extras = groupExtraOptions(extraOptions, {isTableProtected});

  // If we can only insert specific columns, not custom columns, then the "insert multiple columns"
  // UI (selecting multiple columns and inserting) should instead prompt to insert a single column,
  // and we insert to the left/right of the whole selection.
  const shouldAllowMultiple = !disallowCustomColumns;
  const selectedCount = getSelectedCount(selectionRange);

  const insertRange = (direction: InsertDimDirection): SelectionRange | null => {
    if (selectionRange == null) {
      return null;
    }
    if (shouldAllowMultiple) {
      return selectionRange;
    }
    // If we aren't allowed to insert multiple, just insert one at the near end of the selection.
    const insertIndex = direction === 'BEFORE' ? selectionRange.minIndex : selectionRange.maxIndex;
    return {minIndex: insertIndex, maxIndex: insertIndex};
  };

  const computeInsertOptions = (direction: InsertDimDirection): Array<AxisCellMenuInsertOption> | null => {
    // Only allow inserting one field at a time if a special field is to be inserted
    if (
      !insertableColumnInfos ||
      insertableColumnInfos.length === 0 ||
      (selectedCount !== 1 && shouldAllowMultiple)
    ) {
      return null;
    }
    return insertableColumnInfos.map((columnInfo) => ({
      name: columnInfo.columnLabels,
      // For Single Input headers, the inserted column should itself be a Single Input.
      columnInfo: (isSingleInputHeader
        ? withSingleInputColumnInfo(columnInfo)
        : columnInfo) as InsertableColumnInfo,
      dataTestAttrs: {
        component: 'ContextMenu',
        element: direction === 'BEFORE' ? 'insertColumnBefore' : 'insertColumnAfter',
        key: encodeURIComponent(columnInfo.columnLabels).replace(/[^A-Z0-9]/gi, ''),
      },
    }));
  };

  const insertEntry = (direction: InsertDimDirection): AxisCellMenuItemEntry | null => {
    const isOffered =
      direction === 'BEFORE' ? availableActions.canInsertDimBefore : availableActions.canInsertDimAfter;
    if (!isOffered || isTableProtected) {
      return null;
    }
    const item =
      direction === 'BEFORE' ? AxisCellMenuItem.INSERT_DIM_BEFORE : AxisCellMenuItem.INSERT_DIM_AFTER;
    const name = getContextMenuInsertDimsText(
      dim,
      direction,
      selectionRange,
      disallowCustomColumns,
      isSingleInputHeader
    );
    const dataTestAttrs = {component: 'ContextMenu', element: 'insertDimMenu', key: direction};
    if (willInsertExceedTableMaxDims(dim, numDims, selectedCount)) {
      return {
        kind: 'item',
        item,
        name,
        isHidden: false,
        isDisabled: true,
        isClickable: false,
        tooltip: getContextMenuInsertDimsExceedMaxText(dim),
        dataTestAttrs,
        range: null,
        insertOptions: null,
      };
    }
    const isDisabled = direction === 'BEFORE' ? shouldDisableInsertDimBefore : shouldDisableInsertDimAfter;
    return {
      kind: 'item',
      item,
      name,
      isHidden: false,
      isDisabled,
      isClickable: true,
      tooltip: isDisabled ? (disableInsertDimTooltip ?? null) : null,
      dataTestAttrs,
      range: insertRange(direction),
      insertOptions: isDisabled ? null : computeInsertOptions(direction),
    };
  };

  const entries: Array<AxisCellMenuEntry | null> = [
    ...extras.atTop.map((option): AxisCellMenuExtraEntry => ({kind: 'extra', option})),
    {kind: 'separator'},
    {
      kind: 'item',
      item: AxisCellMenuItem.RENAME_DIM,
      name: `Rename ${dim}`,
      isHidden:
        Boolean(columnConstraint && (columnConstraint.editLabelDisabled || columnConstraint.viewer)) ||
        !availableActions.canRenameDim ||
        selectionRange == null ||
        isTableProtected,
      isDisabled: isDimensionProtected,
      isClickable: true,
      tooltip: isDimensionProtected ? 'Cannot rename a locked column' : null,
      dataTestAttrs: {component: 'ContextMenu', element: 'renameDim'},
      range: null,
      insertOptions: null,
    },
    // Change column type option appears right after rename (for custom columns only)
    extras.changeColumnType ? {kind: 'extra', option: extras.changeColumnType} : null,
    {
      kind: 'item',
      item: AxisCellMenuItem.TOGGLE_HEADER,
      name: 'Toggle header',
      isHidden: !availableActions.canToggleHeader,
      isDisabled: false,
      isClickable: true,
      tooltip: null,
      dataTestAttrs: null,
      range: null,
      insertOptions: null,
    },
    {
      kind: 'item',
      item: AxisCellMenuItem.DELETE_TABLE,
      name: 'Delete table',
      isHidden:
        !availableActions.canDeleteTable ||
        !isTableSelected ||
        isLocked ||
        isProtected ||
        shouldDisableDeleteDim,
      isDisabled: false,
      isClickable: true,
      tooltip: null,
      dataTestAttrs: null,
      range: null,
      insertOptions: null,
    },
    {
      kind: 'item',
      item: AxisCellMenuItem.DELETE_DIM,
      name: getContextMenuDeleteDimsText(columnConstraint, selectionRange, dim),
      isHidden: !availableActions.canDeleteDim || (isTableSelected && !allowEmpty) || isTableProtected,
      isDisabled: shouldDisableDeleteDim || isDimensionProtected,
      isClickable: true,
      tooltip: null,
      dataTestAttrs: {component: 'ContextMenu', element: 'deleteDim'},
      range: selectionRange,
      insertOptions: null,
    },
    {kind: 'separator'},
    {
      kind: 'item',
      item: AxisCellMenuItem.LOCK_COLUMN,
      name: 'Lock column',
      isHidden:
        isDimensionProtected ||
        !canSetProtected ||
        disableColumnLocking ||
        isLocked ||
        columnLockBlockedByTableProtection,
      isDisabled: isReadOnly,
      isClickable: true,
      tooltip: isReadOnly ? 'Cannot lock a read only column' : null,
      dataTestAttrs: null,
      range: null,
      insertOptions: null,
    },
    {
      kind: 'item',
      item: AxisCellMenuItem.UNLOCK_COLUMN,
      name: 'Unlock column',
      isHidden: !isDimensionProtected || !canSetProtected || isLocked || columnLockBlockedByTableProtection,
      isDisabled: isReadOnly,
      isClickable: true,
      tooltip: null,
      dataTestAttrs: null,
      range: null,
      insertOptions: null,
    },
    {
      kind: 'item',
      item: AxisCellMenuItem.FREEZE_COLUMN,
      name: 'Freeze up to this column',
      isHidden:
        dim !== TABLE_COLUMN ||
        isLocked ||
        isTableProtected ||
        !availableActions.canFreezeColumn ||
        isAffixed ||
        isSingleInputColumnConstraint,
      isDisabled: false,
      isClickable: true,
      tooltip: null,
      dataTestAttrs: {component: 'ContextMenu', element: 'freezeColumn'},
      range: null,
      insertOptions: null,
    },
    {
      kind: 'item',
      item: AxisCellMenuItem.UNFREEZE_COLUMN,
      name: 'Unfreeze from this column',
      isHidden:
        dim !== TABLE_COLUMN ||
        isLocked ||
        isTableProtected ||
        !availableActions.canUnfreezeColumn ||
        !isAffixed ||
        isSingleInputColumnConstraint,
      isDisabled: false,
      isClickable: true,
      tooltip: null,
      dataTestAttrs: {component: 'ContextMenu', element: 'unfreezeColumn'},
      range: null,
      insertOptions: null,
    },
    ...extras.atMiddleUpper.map((option): AxisCellMenuExtraEntry => ({kind: 'extra', option})),
    {kind: 'separator'},
    insertEntry('BEFORE'),
    insertEntry('AFTER'),
    {kind: 'separator'},
    ...extras.atMiddleLower.map((option): AxisCellMenuExtraEntry => ({kind: 'extra', option})),
    {kind: 'separator'},
    ...extras.atBottomUpper.map((option): AxisCellMenuExtraEntry => ({kind: 'extra', option})),
    {kind: 'separator'},
    ...extras.atBottomLower.map((option): AxisCellMenuExtraEntry => ({kind: 'extra', option})),
  ];

  return entries.filter((entry): entry is AxisCellMenuEntry => entry != null);
}
